import React, { useEffect, useMemo, useState } from 'react'
import {
  useI18n,
  usePersonalInfo,
  useStateList,
  useTalkAboutCoverage,
} from 'contexts/AuthContext'
import { Button, Select } from 'antd'
import _ from 'lodash'
import Lottie from 'lottie-react'
import classnames from 'classnames'
import { getInsuranceProviderForAmplitude, ScreenKey } from './util'
import DropdownSelect, {
  DropdownSelectWithOpen,
} from 'components/DropdownSelect/index'
import { isMobile } from 'utils/basic'
import logoIcon from 'static/img/fiton-care-logo.png'
import logoIconWhite from 'static/img/fiton-care-logo-white.svg'
import bgWelcome from 'static/img/bg-welcome-new-splash.png'
import bgPrescreenPT from 'static/img/bg-prescreen-pt.png'
import closeIcon from 'static/img/ic-close-header.svg'
import checkMarkJson from 'static/json/red-checkmark.json'
import { logAmplitudeEvent, setAmplitudeOption } from 'utils/amplitudeInit'
import CareInput from 'components/CareInput/index'
import { useMetadataOptions } from 'hooks/careHooks'
import {
  getLocalLanguage,
  getStateId,
  setStateId,
} from 'utils/authLocalStorage'
import { providerSupportPromise } from 'apis/FitOnCareAPI'
import { useDeeplink } from 'hooks/useDeeplink'
import { useDarkMode } from 'hooks/useDarkMode'
const { Option } = Select

const getPrescreenInfo = (sku) => {
  switch (sku) {
    case 'pt':
      return {
        title: 'Physical Therapy — Personalized just for you!',
        subtitle: '',
        mobileTitle: 'Physical Therapy — Personalized just for you!',
        tip: 'Answer the following to check eligibility',
        backgroundImage: bgPrescreenPT,
      }
    default:
      return {
        title: 'Welcome to FitOn Care!',
        subtitle:
          "We're here to help you reach your health & wellness goals by pairing you with an experienced Registered Dietitian for personalized nutrition counseling.",
        mobileTitle: 'Nutrition Counseling - Personalized just for you!',
        tip: 'Answer the following to check eligibility',
        backgroundImage: bgWelcome,
      }
  }
}

const getQualifiedInfo = (sku) => {
  switch (sku) {
    case 'pt':
      return {
        title: `Congrats! You're qualified for FitOn Care`,
        subtitle: `Your personalized physical therapy plan is just a few steps away. We’ll gather a bit more info to match you with a licensed Physical Therapist who can guide your recovery.`,
      }
    default:
      return {
        title: `Congrats! You're qualified for FitOn Care`,
        subtitle: `Your personalized nutrition counseling is just a few more steps away. We'll need some more information to get you setup with an experienced Registered Dietitian.`,
      }
  }
}

export const useWelcomeNew = ({ sku }) => {
  const { talkAboutCoverage, setTalkAboutCoverage } = useTalkAboutCoverage()
  const { personalInfo, setPersonalInfo } = usePersonalInfo()
  const [state, setState] = useState(personalInfo.state || {})
  const [insuranceProvider, setInsuranceProvider] = useState(talkAboutCoverage)

  const { insuranceProviders } = useMetadataOptions()
  const { stateList: stateOptions } = useStateList()

  useEffect(() => {
    const localStateId = getStateId()
    if (localStateId) {
      const _state = _.find(
        stateOptions,
        localStateId ? { id: +localStateId } : {}
      )
      setState(_state)
    }
  }, [stateOptions])

  const onCheck = async () => {
    setTalkAboutCoverage({ ...insuranceProvider })
    setPersonalInfo({ state })
    setStateId(state.id)
    const isValid = state.available
    const providerSupport = await providerSupportPromise({
      stateId: state.id,
      insuranceProviderId: insuranceProvider.id,
    })
    let isProviderSupport = false
    switch (sku) {
      case 'pt':
        isProviderSupport = providerSupport.ptAccept
        break
      case 'wlc':
        isProviderSupport = providerSupport.wlcAccept
        break
      default:
        isProviderSupport = providerSupport.accept
        break
    }
    return {
      isValid: isValid && isProviderSupport,
      providerSupport: isProviderSupport,
      isStateValid: isValid,
    }
  }

  const filteredInsuranceProviders = useMemo(() => {
    const _insuranceProviders =
      _.orderBy(insuranceProviders, ['sort'], ['asc']) || []
    return _insuranceProviders
  }, [insuranceProviders])

  return {
    state,
    setState,
    insuranceProviders: filteredInsuranceProviders,
    stateOptions,
    insuranceProvider,
    setInsuranceProvider,
    onCheck,
  }
}

const PrescreenQuestions = (props) => {
  const { sku, showClose, onClose } = props
  const { theme } = useDeeplink()
  const { i18n } = useI18n()
  const [isQualified, setIsQualified] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [providerOptionOpen, setProviderOptionOpen] = useState(false)

  const { isForcedDarkMode } = useDarkMode()

  const {
    state,
    setState,
    insuranceProvider,
    setInsuranceProvider,
    insuranceProviders,
    stateOptions,
    onCheck,
  } = useWelcomeNew({ sku })

  const currentLogoIcon = useMemo(() => {
    return isForcedDarkMode ? logoIconWhite : logoIcon
  }, [isForcedDarkMode])

  useEffect(() => {
    logAmplitudeEvent('Screen View: Onboarding - Welcome', {
      Type: 'Prescreen Questions',
      ...(theme ? { theme: theme } : {}),
      sku: sku,
      language: getLocalLanguage(),
    })
  }, [sku, theme])

  const openNextScreen = () => {
    props.setCurrentKey(ScreenKey.PERSONAL_INFO)
  }

  const onCheckClick = async () => {
    setIsLoading(true)
    const { isValid, providerSupport } = await onCheck()
    setIsLoading(false)
    props.setSupportForNewWelcome({ isValid, providerSupport })
    if (isValid) {
      setIsQualified(true)
      setTimeout(() => {
        openNextScreen()
      }, 3000)
    } else {
      openNextScreen()
    }
    const provider = insuranceProvider.otherText
      ? `${insuranceProvider.name} - ${insuranceProvider.otherText}`
      : insuranceProvider.name
    const params = {
      State: state.name,
      Provider: provider,
      language: getLocalLanguage(),
      sku: sku,
      ...(theme ? { theme: theme } : {}),
    }

    logAmplitudeEvent('Click: Onboarding - Welcome', params)
    setAmplitudeOption(
      'Insurance - Provider',
      getInsuranceProviderForAmplitude(insuranceProvider) || provider
    )
    setAmplitudeOption('State', state.name)
  }

  const isCheckEnable = useMemo(() => {
    const isValidProvider =
      insuranceProvider?.name === 'Other'
        ? !_.isEmpty(insuranceProvider.otherText)
        : !_.isEmpty(insuranceProvider)

    return !_.isEmpty(state) && isValidProvider
  }, [insuranceProvider, state])

  const elementIntoView = (id, delay, block) => {
    setTimeout(() => {
      const element = document.getElementById(id)
      if (element)
        try {
          element.scrollIntoView({
            behavior: 'smooth',
            block: block || 'center',
          })
        } catch (e) {
          element.scrollIntoView(false)
        }
    }, delay || 1000)
  }

  const prescreenQuestionInfo = useMemo(() => {
    return getPrescreenInfo(sku)
  }, [sku])

  const qualifiedInfo = useMemo(() => {
    return getQualifiedInfo(sku)
  }, [sku])

  const welcomeRender = () => {
    return (
      <>
        <div
          className='header'
          hidden={!isMobile()}
          style={isMobile() ? { height: '26px', minHeight: '26px' } : {}}
        />
        <img
          alt=''
          src={prescreenQuestionInfo.backgroundImage}
          className='bg-welcome'
          style={isMobile() ? { display: 'none' } : {}}
        />
        <div
          className='content-sec'
          id='id-content-sec-welcome-new'
          style={
            isMobile()
              ? {
                  minHeight: 'calc(100vh - 26px)',
                  marginTop: '26px',
                  borderRadius: '24px 24px 0 0',
                }
              : {}
          }
        >
          {showClose && (
            <img
              src={closeIcon}
              alt=''
              className='close-icon'
              onClick={onClose}
              style={{ top: '13px', transform: 'none' }}
            />
          )}
          <div className='desktop-sec' hidden={isMobile()}>
            <img src={currentLogoIcon} alt='' className='logo-icon' />
            <div className='title'>{i18n(prescreenQuestionInfo.title)}</div>
            <div className='subtitle'>
              {i18n(prescreenQuestionInfo.subtitle)}
            </div>
          </div>
          <div className='mobile-sec' hidden={!isMobile()}>
            <img src={currentLogoIcon} alt='' className='logo-icon' />
            <img
              alt=''
              src={prescreenQuestionInfo.backgroundImage}
              className='bg-mobile-welcome'
            />
            <div className='title'>
              {i18n(prescreenQuestionInfo.mobileTitle)}
            </div>
          </div>
          <div className='answer-title'>{i18n(prescreenQuestionInfo.tip)}</div>
          <div className='answer-sec'>
            <DropdownSelect
              {...{
                placeholder: i18n('Select state'),
                options: stateOptions,
                setValue: (value) => {
                  const currentOption = stateOptions.find(
                    (option) => option.id === value
                  )
                  setState(currentOption)
                },
                value: state && state.id,
                className: `${isMobile() ? 'm-t-8' : 'm-t-0'}`,
                render: (
                  <>
                    {stateOptions.map((option, index) => (
                      <Option
                        className='state-item'
                        value={option.id}
                        key={index}
                      >
                        {option.name}
                      </Option>
                    ))}
                  </>
                ),
              }}
            />
            {!_.isEmpty(state) && (
              <DropdownSelectWithOpen
                {...{
                  placeholder: i18n('Select your insurance provider'),
                  value:
                    insuranceProvider &&
                    (insuranceProvider.name === 'Other'
                      ? insuranceProvider.otherText || insuranceProvider.name
                      : insuranceProvider.name),
                  className: `${isMobile() ? 'm-t-8' : 'm-t-18'}`,
                  open: providerOptionOpen,
                  dropdownRender: () => (
                    <>
                      {insuranceProviders.map((item, index) => (
                        <div
                          className={classnames('custom-item', {
                            active: insuranceProvider?.id === item.id,
                          })}
                          onClick={() => {
                            const currentOption = insuranceProviders.find(
                              (option) => option.id === item.id
                            )
                            if (currentOption.name === 'Other') {
                              elementIntoView(
                                'id-insurance-bottom',
                                700,
                                'start'
                              )
                            } else {
                              setProviderOptionOpen(false)
                            }
                            setInsuranceProvider(currentOption)
                          }}
                        >
                          <div className='normal-item'>
                            {item.name === 'Other' ||
                            item.name === 'I will not be using insurance'
                              ? i18n(item.name)
                              : item.name}
                          </div>
                        </div>
                      ))}
                      {insuranceProvider.name === 'Other' && (
                        <CareInput
                          {...{
                            inputValue: insuranceProvider.otherText || '',
                            setInputValue: (value) => {
                              const regex = /^[a-zA-Z0-9\s]*$/
                              if (regex.test(value)) {
                                setInsuranceProvider({
                                  ...insuranceProvider,
                                  otherText: value,
                                })
                              }
                            },
                            placeholder: i18n('Please specify'),
                            focus: insuranceProvider.name === 'Other',
                            maxLength: 50,
                            className: 'input-sec-padding m-t-12',
                          }}
                        />
                      )}
                      <div
                        id='id-insurance-bottom'
                        className='bottom-div'
                      ></div>
                    </>
                  ),
                  onDropdownVisibleChange: (visible) => {
                    setProviderOptionOpen(visible)
                  },
                }}
              />
            )}
          </div>
          <Button
            className='btn-primary full-width-btn check-btn-desktop'
            onClick={onCheckClick}
            disabled={!isCheckEnable || isLoading}
          >
            {i18n('Check')}
          </Button>
        </div>
        <Button
          className='btn-primary full-width-btn check-btn-mobile'
          onClick={onCheckClick}
          disabled={!isCheckEnable || isLoading}
        >
          {i18n('Check')}
        </Button>
      </>
    )
  }

  const qualifiedRender = () => {
    return (
      <>
        <img
          alt=''
          src={prescreenQuestionInfo.backgroundImage}
          className='bg-welcome'
          style={isMobile() ? { display: 'none' } : {}}
        />
        <div
          className='content-sec'
          style={
            isMobile()
              ? {
                  minHeight: 'calc(100vh - 26px)',
                  marginTop: '26px',
                  borderRadius: '24px 24px 0 0',
                }
              : { minHeight: '518px' }
          }
        >
          {showClose && (
            <img
              src={closeIcon}
              alt=''
              className='close-icon'
              onClick={onClose}
              style={{ top: '13px', transform: 'none' }}
            />
          )}
          <div className='desktop-sec' hidden={isMobile()}>
            <img src={currentLogoIcon} alt='' className='logo-icon' />
            <div className='title'>{i18n(qualifiedInfo.title)}</div>
          </div>
          <div className='mobile-sec' hidden={!isMobile()}>
            <img src={currentLogoIcon} alt='' className='logo-icon' />
            <img
              alt=''
              src={prescreenQuestionInfo.backgroundImage}
              className='bg-mobile-welcome'
            />
            <div className='title'>{i18n(qualifiedInfo.title)}</div>
          </div>
          <Lottie
            animationData={checkMarkJson}
            loop={false}
            className='red-check-mark'
          />
          <div
            className='subtitle bottom-info'
            style={{ marginBottom: '40px' }}
          >
            {i18n(qualifiedInfo.subtitle)}
          </div>
        </div>
      </>
    )
  }

  return (
    <div className='welcome-new'>
      {isQualified ? qualifiedRender() : welcomeRender()}
    </div>
  )
}

export default PrescreenQuestions
